import { MutableRefObject } from 'react';

import Client from '@rbi-ctg/chrome-os-tcp-client';
import { Action, SetState } from '@rbi-ctg/frontend';
import { IPaymentResponse } from '@rbi-ctg/tender-retail-client';
import { ITransactionResult } from '@rbi-ctg/verifone-point-connect';
import { IBillingAddressInput } from 'generated/rbi-graphql';
import { IErrorModal } from 'hooks/use-error-modal';
import { PaymentISOs } from 'utils/form/constants';
import { IFraudPreventionValues } from 'utils/payment';

import { IPairingData } from './hooks/use-register-request';
import { ISessionOptions } from './hooks/use-session-request';

export interface IPaymentRequest<T = ITransactionOptions> {
  onDecline: () => Promise<void> | void;
  onCancel: () => Promise<void> | void;
  onSuccess: (result: ITransactionResult | IPaymentResponse) => Promise<void> | void;
  session: Omit<ISessionOptions, 'invoice'>;
  transaction: T;
}

export interface ITransactionOptions {
  transAmount: number; // the amount in cents of the transaction (including tax/tip)
  taxAmount?: number; // the amount in cents of tax included in the total
  tipAmount?: number; // the amount in cents of tip included in the total; default 0
  trainingMode?: boolean; // whether or not to run the transaction with training mode on; default true
  forceFlag?: boolean; // whether or not to override duplicate checking on the pin pad; default false
}

export interface IUsePaymentRequestArgs {
  counter: MutableRefObject<number>;
  hostname: string;
  finishSession: (client: Client) => Promise<void>;
  openErrorDialog: (args: IErrorModal) => void;
  pairingData: IPairingData;
  port: number;
  startSession: (options: ISessionOptions) => Promise<Client>;
  setBusy: SetState<boolean>;
  terminalId: string;
}

export interface IPaymentRequestState {
  loading: boolean;
  error: Error | null;
  request: IPaymentRequest | null;
  result: ITransactionResult | null;
  session: boolean;
  transactionNumber: number;
}

export enum ActionType {
  PAYMENT_REQUESTED = 'PAYMENT_REQUESTED',
  PAYMENT_SUCCESS = 'PAYMENT_SUCCESS',
  PAYMENT_ERROR = 'PAYMENT_ERROR',
  SESSION_STARTED = 'SESSION_STARTED',
  SESSION_FINISHED = 'SESSION_FINISHED',
  SESSION_ERROR = 'SESSION_ERROR'
}

export const onPaymentRequested = (
  options: IPaymentRequest
): Action<ActionType.PAYMENT_REQUESTED, IPaymentRequest> => ({
  payload: options,
  type: ActionType.PAYMENT_REQUESTED
});

export const onPaymentSuccess = (
  result: ITransactionResult
): Action<ActionType.PAYMENT_SUCCESS, ITransactionResult> => ({
  payload: result,
  type: ActionType.PAYMENT_SUCCESS
});

export const onPaymentError = (error: Error): Action<ActionType.PAYMENT_ERROR, Error> => ({
  error: true,
  payload: error,
  type: ActionType.PAYMENT_ERROR
});

export const onSessionStarted = (): Action<ActionType.SESSION_STARTED> => ({
  type: ActionType.SESSION_STARTED
});

export const onSessionFinished = (): Action<ActionType.SESSION_FINISHED> => ({
  type: ActionType.SESSION_FINISHED
});

export const onSessionError = (error: Error): Action<ActionType.SESSION_ERROR, Error> => ({
  payload: error,
  type: ActionType.SESSION_ERROR
});

export type Actions = ReturnType<
  | typeof onPaymentRequested
  | typeof onPaymentSuccess
  | typeof onPaymentError
  | typeof onSessionStarted
  | typeof onSessionFinished
  | typeof onSessionError
>;

// @TODO This will be generated from Sanity
export type CardType = 'AMEX' | 'DINERS_CLUB' | 'DISCOVER' | 'JCB' | 'MASTERCARD' | 'VISA';

/**
 * @TODO This can be removed in favor of the generated `CartPaymentCardType` enum
 */
export enum CardTypes {
  AMEX = 'AMEX',
  APPLE_PAY = 'APPLE_PAY',
  GOOGLE_PAY = 'GOOGLE_PAY',
  DINERS_CLUB = 'DINERS_CLUB',
  DISCOVER = 'DISCOVER',
  JCB = 'JCB',
  MASTERCARD = 'MASTERCARD',
  PAYPAL = 'PAYPAL',
  VISA = 'VISA'
}

/**
 * These card types are the ones supported by our Apple Pay plugin (frontend/ios/App/App/ApplePayPlugin.swift)
 */
export enum ApplePayCardTypes {
  AMEX = 'AmEx',
  DISCOVER = 'Discover',
  MASTERCARD = 'MasterCard',
  VISA = 'Visa'
}

export enum PaymentType {
  CASH = 'CASH',
  DIGITAL = 'DIGITAL',
  GIFT = 'GIFT'
}

export enum PaymentProcessor {
  ADYEN = 'ADYEN',
  FIRSTDATA = 'FIRSTDATA'
}

export interface ICredit {
  alias: string;
  /**
   * The BE does not currently standardize card type, for example upper case vs lower case.
   * Eventually we want to align the FE and BE.
   *
   * TODO: Use type from `IUserAccountsQuery` and not force our own.
   */
  cardType: CardTypes | string;
  expiryMonth: string;
  expiryYear: string;
  panToken: string;
}

export interface IPrepaid {
  alias: string;
  cardNumber: string;
  currentBalance: number;
  expiryMonth: string;
  expiryYear: string;
  feFormattedCurrentBalance: number | null;
  shuffledCardNumber: string;
}

export interface IPaymentMethod {
  accountIdentifier: string | null;
  fdAccountId: string | null;
  chaseProfileId: string | null;
  credit: ICredit | null;
  selected?: boolean;
  prepaid: IPrepaid | null;
}

export interface IAddAccount {
  addAccount: IPaymentMethod;
}

export interface IAddress {
  locality: string;
  postalCode: string;
  region: string;
  streetAddress: string;
  unitNumber?: string;
}

export interface IAddPaymentMethodInput {
  billingAddress: IBillingAddressInput;
  cardNumber: string;
  chaseProfileId?: string;
  expiryDate: {
    month: string;
    year: string;
  };
  nameOnCard: string;
  securityCode: string;
  accountToDelete?: string;
}

export interface IAddPaymentMethodOptions {
  skipErrorDialogOnError?: boolean;
}

export interface IDeleteAccount {
  deleteAccount: boolean;
}

export interface IGetAccounts {
  userAccounts?: {
    count?: number;
    accounts?: IPaymentMethod[];
  };
}

// @todo validate type
export interface IGetNonce {
  token: {
    tokenId: string;
  };
}

export interface IApplePayDetails {
  signature: string;
  applicationData: string;
  country: PaymentISOs;
  data: string;
  decryptAlias: string;
  ephemeralPublicKey: string;
  formatted: string;
  paymentData: string;
  primary: boolean;
  publicKeyHash: string;
  transactionId: string;
  type: string;
  version: string;
  billingAddress: IBillingAddressInput;
  paymentMethodData?: IPaymentMethodData;
}

export interface IGooglePayDetails {
  signature: string;
  country: PaymentISOs;
  data: string;
  formatted: string;
  primary: boolean;
  type: string;
  version: string;
  billingAddress: IBillingAddressInput;
  paymentMethodData?: IPaymentMethodData;
  paymentData: string;
}

interface IPaymentMethodData {
  paymentType: string;
  displayName: string;
}

export interface IReloadPrepaidCard {
  countryCode: string;
  fundValue: number;
  prepaidBalance: number;
  fdAccountId?: string | null;
  prepaidFdAccountId?: string | null;
  currencyCode?: string;
  cardType?: string | null;
  applePayDetails?: IApplePayDetails;
  googlePayDetails?: IGooglePayDetails;
}

export interface IReloadPrepaidCardConfig {
  swallowError?: boolean;
}

export interface IReloadDetails {
  prepaidsReload: {
    currentBalance: number;
    fdAccountId: string;
    fdCorrelationId: string;
    transactionId: string;
  };
}

export interface IPaymentDetails {
  accountIdentifier?: string;
  encryptedCN?: string;
  encryptedEM?: string;
  encryptedEY?: string;
  encryptedSC?: string;
  fdAccessToken?: string;
  fdNonce?: string;
  creditType?: string;
  fraudPreventionValues?: IFraudPreventionValues;
  applePayDetails?: IApplePayDetails;
  googlePayDetails?: IGooglePayDetails;
}
